import { apiRootUrl } from '../assets/constants';
import { showApiErrorAlert } from '../functions/api';

// types
type TopicContentType = {
    body?: string,
    openRange: number,
    postStatus: number,
    title: string,
    topicUsers: number[],
    userId: number,
}

type TopicMember = {
    userId: number,
    userName: string,
}

type UserTopicType = {
    body: string,
    commentCount: number,
    openRange: number
    postStatus: number,
    postTime: string,
    title: string,
    topicId: number,
    topicMembers: null | {
        email: string,
        userId: number,
        userName: string,
    }[],
    updatedTime: string,
    userId: number,
    userName: string,
}

type SuccessResponse = {
    status: number;
    data: {
        topicId: number;
    };
}

type ErrorResponse = {
    status: number,
    error: {
        code: string,
        message: string,
        validationErrors: {
            key: string,
            message: string
        }[]
        | null
    }
}

type ResponseType = SuccessResponse | ErrorResponse;


// トピックの取得
export const getTopic = (topicId: string) => {
    const api = apiRootUrl + "/topics/" + topicId;
    const options = {
        method: "GET",
    }
    return fetch(api, options)
        .then((response) => response.json())
        .then((result) => {
            // アラート表示
            if (result.status === 500) {
                showApiErrorAlert(result.status, "トピックの取得");
            }
            // エラー処理
            if (result.status !== 200) {
                throw new Error(result.status);
            }
            // 成功処理
            if (result.status === 200) {
                return result.data;
            }
        })
        .catch((error) => {
            console.log(error);
        });
};

// トピックメンバー一覧の取得
// 対象のトピックが「公開トピック」の場合は、Roomメンバーを、「招待制トピック」の場合は招待メンバーが返されます。
// ※参加者メンバーとしてトピック作成者も含まれた状態で値が返されます。
export const getTopicMembers = (topicId: string): Promise<TopicMember[]> => {
    const api = apiRootUrl + "/topics/" + topicId + "/members";
    const options = {
        method: "GET",
    }
    return fetch(api, options)
        .then((response) => response.json())
        .then((result) => {
            // アラート表示
            if (result.status === 500) {
                showApiErrorAlert(result.status, "トピックメンバー一覧の取得");
            }
            // エラー処理
            if (result.status !== 200) {
                throw new Error(result.status);
            }
            // 成功処理
            if (result.status === 200) {
                return result.data;
            }
        })
        .catch((error) => {
            console.log(error);
        });
};

// トピックコメント一覧の取得
export const getTopicComments = (topicId: string) => {
    const api = apiRootUrl + "/topics/" + topicId + "/comments";
    const options = {
        method: "GET",
    }
    return fetch(api, options)
        .then((response) => response.json())
        .then((result) => {
            // アラート表示
            if (result.status === 500) {
                showApiErrorAlert(result.status, "トピックコメント一覧の取得");
            }
            // エラー処理
            if (result.status !== 200 && result.status !== 204) {
                throw new Error(result.status);
            }
            // 成功処理
            if (result.status === 200) {
                return result.data;
            }
            if (result.status === 204) {
                return [];
            }
        })
        .catch((error) => {
            console.log(error);
        });
};

// トピックの作成
export const addTopic = (roomId: string, content: TopicContentType): Promise<ResponseType | void> => {
    const api = apiRootUrl + "/topics/" + roomId;
    const options = {
        body: JSON.stringify(content),
        headers: {
            "Content-Type": "application/json",
        },
        method: "POST",
    }
    return fetch(api, options)
        .then((response) => response.json())
        .then((result) => {
            // アラート表示
            if (result.status === 500) {
                showApiErrorAlert(result.status, "トピックの作成");
            }
            // エラー処理
            if (result.status !== 200 && result.status !== 422) {
                throw new Error(result.status);
            }
            // 成功もしくは入力エラーが検出された場合は、結果を返す
            if (result.status === 200 || result.status === 422) {
                return result;
            }
        })
        .catch((error) => {
            console.log(error);
        });
};

// トピックの更新
export const updateTopic = (topicId: string, content: TopicContentType): Promise<ResponseType | void> => {
    const api = apiRootUrl + "/topics/" + topicId;
    const options = {
        body: JSON.stringify(content),
        headers: {
            "Content-Type": "application/json",
        },
        method: "PUT",
    }
    return fetch(api, options)
        .then((response) => response.json())
        .then((result) => {
            // アラート表示
            if (result.status === 403 || result.status === 404 || result.status === 500) {
                showApiErrorAlert(result.status, "トピックの更新");
            }
            // エラー処理
            if (result.status !== 200 && result.status !== 422) {
                throw new Error(result.status);
            }
            // 成功もしくは入力エラーが検出された場合は、結果を返す
            if (result.status === 200 || result.status === 422) {
                return result;
            }
        })
        .catch((error) => {
            console.log(error);
        });
};

// トピックの削除
export const deleteTopic = (topicId: string) => {
    const api = apiRootUrl + "/topics/" + topicId;
    const options = {
        method: "DELETE",
    }
    return fetch(api, options)
        .then((response) => response.json())
        .then((result) => {
            // アラート表示
            if (result.status === 404 || result.status === 500) {
                showApiErrorAlert(result.status, "トピックの削除");
            }
            // エラー処理
            if (result.status !== 200 && result.status !== 204) {
                throw new Error(result.status);
            }
            // 成功処理
            if (result.status === 200) {
            }
        })
        .catch((error) => {
            console.log(error);
        });
};

// ユーザー投稿トピック一覧の取得
export const getUserTopics = (userId: string, roomId: string, topicstype: string): Promise<UserTopicType[] | void> => {
    const api = apiRootUrl + "/topics/management/" + userId + "/" + roomId + "?topicstype=" + topicstype;
    const options = {
        method: "GET",
    }
    return fetch(api, options)
        .then((response) => response.json())
        .then((result) => {
            // アラート表示
            if (result.status === 500) {
                showApiErrorAlert(result.status, "ユーザー投稿トピック一覧の取得");
            }
            // エラー処理
            if (result.status !== 200) {
                throw new Error(result.status);
            }
            // 成功処理
            if (result.status === 200) {
                return result.data;
            }
        })
        .catch((error) => {
            console.log(error);
        });
};
